import classnames from "classnames";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

import { isFundingSourceCashAndSecurities } from "../../../../helpers";
import Icon from "../../../Icon";

const CustomSelectInput = ({
  typeahead,
  showOptions,
  handleShowOption,
  handleUserInput,
  handleOnActive,
  items,
  label,
  trigger,
  disabled,
  readOnly,
  required,
  value,
  name,
  onChange,
  confirmOptions,
}) => {
  const [selectedLabel, setSelectedLabel] = useState(value);
  const [isOpen, setIsOpen] = useState(showOptions);
  const [filteredOptions, setFilteredOptions] = useState(items);
  const [selectedOption, setSelectedOption] = useState(null);

  const selectRef = useRef(null);
  const selectTriggerRef = useRef(null);

  const {
    withConfirmation,
    hasConfirmed,
    triggerConfirmationModal,
    setNewSelectedValue,
  } = confirmOptions || {
    withConfirmation: false,
    hasConfirmed: false,
    triggerConfirmationModal: () => {},
    setNewSelectedValue: () => {},
  };

  const hasInvalidValue = (value) => {
    return typeof value === "undefined" || value?.trim() === "";
  };

  const handleValidateField = () => {
    trigger(name);
  };

  const handleOpen = (showOptions) => {
    // Set state and value
    if (!readOnly) {
      setIsOpen(showOptions);
      handleShowOption(showOptions);
    }

    // If options list is closed, set error if no value is selected
    if (!showOptions) handleCheckValue(selectedLabel);
  };

  const handleCheckValue = (value) => {
    const val = typeahead ? value : selectedLabel;

    // If value is invalid, set error
    if (hasInvalidValue(val) && typeahead) {
      handleUserInput(null);
      setSelectedLabel("");
    }

    handleValidateField();
  };

  const handleSetFocus = () => {
    selectTriggerRef.current.focus();
  };

  const handleClick = () => {
    if (!typeahead) {
      handleOpen(!isOpen && items.length > 0);
      return;
    }

    handleOnActive(true);
  };

  const handleSelect = (e) => {
    const newSelectedValue = e.target.dataset.value;
    const showModal = triggerConfirmationModal(selectedLabel, newSelectedValue);

    if (withConfirmation && showModal) {
      setSelectedOption(e);
      setNewSelectedValue(newSelectedValue);
      return;
    }

    if (isFundingSourceCashAndSecurities(newSelectedValue))
      setNewSelectedValue(newSelectedValue);
    proceedSelectChange(e);
  };

  const proceedSelectChange = useCallback((e) => {
    // On Option selection, set local state value
    setSelectedLabel(e.target.innerText);

    onChange && onChange(e.target.dataset.value);

    handleOpen(false); // close te options list
    handleCheckValue(e.target.dataset.value);
    handleSetFocus(); // set focus on trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const inputValue = e.currentTarget.value;
    setSelectedLabel(inputValue);

    let results = [];

    if (inputValue?.trim() !== "") {
      results = items.filter(
        (item) =>
          item.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
      );
    }

    if (results.length > 0) {
      setFilteredOptions(results);
      handleOpen(true);
    } else {
      onChange && onChange();
      setFilteredOptions([]);
      handleOpen(false);
    }

    handleUserInput(inputValue);
  };

  const handleKeyUp = (e) => {
    const key = e.key || e.keyCode;

    if (isOpen) {
      if (key === "Escape") {
        handleOpen(false);
        handleSetFocus();
        handleValidateField();
      } else if (key === "ArrowUp") {
        // TODO: handle item selection
        console.log("up");
      } else if (key === "ArrowDown") {
        // TODO: handle item selection
        console.log("down");
      }
    }
  };

  const handleOutsideClick = useCallback((e) => {
    if (!selectRef.current || selectRef.current.contains(e.target)) {
      return;
    }
    handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (items.length > 0) {
      const item = items.find((item) => item.value === value);
      setSelectedLabel(item?.label || "");

      if (!typeahead) {
        if (value === "") {
          setSelectedLabel("");
        }
        setFilteredOptions(items);
      }
    }
  }, [items, value, selectedLabel, typeahead]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("click", handleOutsideClick);
      return () => document.removeEventListener("click", handleOutsideClick);
    }
  }, [isOpen, handleOutsideClick]);

  useEffect(() => {
    if (hasConfirmed) {
      proceedSelectChange(selectedOption);
    }
  }, [hasConfirmed, selectedOption, proceedSelectChange]);

  return (
    <div className="custom-select" ref={selectRef}>
      <div
        className={classnames("custom-select__trigger", {
          "custom-select__trigger--disabled": disabled || readOnly,
        })}
      >
        <input
          id={`${name}-button`}
          name={name}
          type={typeahead ? "text" : "button"}
          autoComplete="off"
          value={selectedLabel || ""}
          className="custom-select__button"
          aria-haspopup="listbox"
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          onChange={handleChange} // typeahead
          disabled={disabled}
          readOnly={readOnly}
          ref={selectTriggerRef}
          aria-label={label}
        />
        {!typeahead && (
          <Icon
            name="system-open-down"
            className="form-input-icon custom-select__arrow"
          />
        )}
      </div>

      {isOpen && (
        <div className="custom-select__options">
          <ul
            className="custom-select__listbox scrollable-container"
            role="listbox"
            tabIndex="-1"
            aria-labelledby={`${name}-button`}
          >
            {filteredOptions.length !== 0 ? (
              <>
                {!required && (
                  <li
                    className="custom-select__item is-default"
                    role="option"
                    aria-selected={!!selectedLabel}
                    data-value=""
                    onClick={handleSelect}
                    onKeyUp={handleKeyUp}
                  >
                    {label}
                  </li>
                )}

                {filteredOptions.map((item, i) => {
                  const isSelected = item.value === value;
                  return (
                    <li
                      className={classnames("custom-select__item", {
                        "is-selected": isSelected,
                      })}
                      role="option"
                      aria-selected={isSelected}
                      key={i}
                      data-value={item.value}
                      onClick={handleSelect}
                      tabIndex={isSelected ? "-1" : "0"}
                      onKeyUp={handleKeyUp}
                      disabled={item.disabled}
                    >
                      {item.label}
                    </li>
                  );
                })}
              </>
            ) : (
              <li className="custom-select__item no-result">No match found.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

CustomSelectInput.propTypes = {
  typeahead: PropTypes.bool,
  showOptions: PropTypes.bool,
  handleShowOption: PropTypes.func,
  handleUserInput: PropTypes.func,
  handleOnActive: PropTypes.func,
  items: PropTypes.array,
  label: PropTypes.string,
  trigger: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  confirmOptions: PropTypes.object,
};

export default CustomSelectInput;
